import { createError } from '@common/neo.tax';

import { formatContributorNameWithEmail, ProviderLogo } from '@neo.tax/shared';

import { ContributorTpUser, TicketingUser } from './hooks/useTicketingUsers';

export type OptionType = {
  icon: JSX.Element;
  label: string;
  value: string;
};

export const transformTicketingProviderUserToOption = (ticketingProviderUser: ContributorTpUser): OptionType => ({
  icon: <ProviderLogo provider={ticketingProviderUser.ticketingProvider} size={20} />,
  label: ticketingProviderUser.fullName ?? '',
  value: ticketingProviderUser.id,
});

export const transformTicketingUserToOption = (ticketingUser: TicketingUser): OptionType => ({
  icon: <ProviderLogo provider={ticketingUser.provider} size={20} />,
  label: formatContributorNameWithEmail(ticketingUser.name, ticketingUser.email),
  value: ticketingUser.id,
});

export const getSourceTicketingProviderUserId = (
  options: OptionType[],
  existingAccounts: { id: string }[],
  allAccounts: { id: string; contributorId: string }[],
): string => {
  const optionForSourceTicketingProviderUser = options.filter(
    (option) => !existingAccounts.find((linkedAccount) => linkedAccount.id === option.value),
  );
  if (optionForSourceTicketingProviderUser.length > 1) {
    throw createError(
      'getSourceTicketingProviderUserId',
      'More than a single source ticketing provider user was selected',
    );
  }
  const [sourceTicketingProviderUserOption] = optionForSourceTicketingProviderUser;
  const sourceTicketingProviderUser = allAccounts.find(
    (allAccount) => allAccount.id === sourceTicketingProviderUserOption.value,
  );
  if (!sourceTicketingProviderUser) {
    throw createError('getSourceTicketingProviderUserId', 'Missing source ticketing provider user');
  }
  return sourceTicketingProviderUser.id;
};

export const getRemovedTicketingProviderUserId = (
  options: OptionType[],
  existingAccounts: { id: string }[],
): string => {
  const removedAccounts = existingAccounts.filter(
    (linkedAccount) => !options.find((option) => option.value === linkedAccount.id),
  );
  if (removedAccounts.length > 1) {
    throw createError('getRemovedTicketingProviderUserId', 'More than a single ticketing provider user was removed');
  }
  if (removedAccounts.length === 0) {
    throw createError('getRemovedTicketingProviderUserId', 'Missing removed ticketing provider user');
  }
  return removedAccounts[0].id;
};
